import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Save, 
  Building, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Instagram, 
  Facebook, 
  Clock, 
  DollarSign, 
  Truck, 
  MapPinIcon, 
  Hash, 
  FileText, 
  Settings,
  Loader2,
  Info,
  CheckCircle,
  AlertCircle,
  Upload,
  Image as ImageIcon,
  Trash2
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { config } from '../../config/environment';

interface Empresa {
  id: number;
  nome: string;
  cnpj: string;
  endereco: string;
  telefone: string;
  email: string;
  logo_url?: string;
  tema: string;
  google_maps_api_key?: string;
  endereco_completo?: string;
  cep?: string;
  cidade?: string;
  estado?: string;
  bairro?: string;
  numero?: string;
  complemento?: string;
  whatsapp?: string;
  instagram?: string;
  facebook?: string;
  site?: string;
  descricao?: string;
  horario_atendimento?: string;
  valor_frete_minimo?: number;
  valor_por_km?: number;
  raio_entrega?: number;
  tempo_preparo_padrao?: number;
  tempo_por_km?: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

interface ConfigForm {
  // Informações básicas
  nome: string;
  cnpj: string;
  email: string;
  telefone: string;
  whatsapp: string;
  tema: string;
  
  // Endereço
  endereco: string;
  endereco_completo: string;
  cep: string;
  cidade: string;
  estado: string;
  bairro: string;
  numero: string;
  complemento: string;
  
  // Redes sociais e site
  instagram: string;
  facebook: string;
  site: string;
  
  // Descrição e horários
  descricao: string;
  horario_atendimento: string;
  
  // Configurações de frete
  valor_frete_minimo: number;
  valor_por_km: number;
  raio_entrega: number;
  
  // Configurações de tempo
  tempo_preparo_padrao: number;
  tempo_por_km: number;
  
  // API e outros
  google_maps_api_key: string;
  ativo: boolean;
}

const ConfiguracoesPage: React.FC = () => {
  const [saving, setSaving] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState<'basicas' | 'endereco' | 'redes' | 'descricao' | 'frete' | 'tecnicas'>('basicas');
  const { usuario, token } = useAuth();

  // Estados para Logo da Empresa
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removerLogo, setRemoverLogo] = useState<boolean>(false);

  const obterUrlImagem = (caminho?: string | null) => {
    if (!caminho) return null;
    if (caminho.startsWith('http://') || caminho.startsWith('https://')) return caminho;
    const baseUrl = config.api.baseUrl || '';
    return `${baseUrl}${caminho.startsWith('/') ? '' : '/'}${caminho}`;
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      setLogoFile(file);
      setRemoverLogo(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoverLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoverLogo(true);
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ConfigForm>();

  // Verificar autenticação
  useEffect(() => {
    console.log('🔍 [CONFIG] Estado de autenticação:', { usuario, token: token ? 'Presente' : 'Ausente' });
  }, [usuario, token]);

  // Estado para dados da empresa
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Carregar dados da empresa
  useEffect(() => {
    const carregarEmpresa = async () => {
      if (!token) {
        console.log('❌ [CONFIG] Token não disponível, aguardando...');
        return;
      }

      console.log('🔍 [CONFIG] Buscando dados da empresa...');
      console.log('🔍 [CONFIG] Token disponível:', token ? 'Sim' : 'Não');
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get('/api/empresas/1');
        console.log('✅ [CONFIG] Dados da empresa recebidos:', response.data);
        setEmpresa(response.data);
      } catch (error) {
        console.error('❌ [CONFIG] Erro ao buscar dados da empresa:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    carregarEmpresa();
  }, [token]);

  // Atualizar formulário quando dados carregarem
  useEffect(() => {
    console.log('🔍 [CONFIG] useEffect - empresa:', empresa, 'loading:', loading, 'error:', error);
    if (empresa) {
      console.log('✅ [CONFIG] Atualizando formulário com dados da empresa:', empresa);
      if (empresa.logo_url) {
        setLogoPreview(obterUrlImagem(empresa.logo_url));
      } else {
        setLogoPreview(null);
      }
      setLogoFile(null);
      setRemoverLogo(false);

      reset({
        // Informações básicas
        nome: empresa.nome || '',
        cnpj: empresa.cnpj || '',
        email: empresa.email || '',
        telefone: empresa.telefone || '',
        whatsapp: empresa.whatsapp || '',
        tema: empresa.tema || 'dark',
        
        // Endereço
        endereco: empresa.endereco || '',
        endereco_completo: empresa.endereco_completo || '',
        cep: empresa.cep || '',
        cidade: empresa.cidade || '',
        estado: empresa.estado || '',
        bairro: empresa.bairro || '',
        numero: empresa.numero || '',
        complemento: empresa.complemento || '',
        
        // Redes sociais e site
        instagram: empresa.instagram || '',
        facebook: empresa.facebook || '',
        site: empresa.site || '',
        
        // Descrição e horários
        descricao: empresa.descricao || '',
        horario_atendimento: empresa.horario_atendimento || '',
        
        // Configurações de frete
        valor_frete_minimo: empresa.valor_frete_minimo || 0,
        valor_por_km: empresa.valor_por_km || 0,
        raio_entrega: empresa.raio_entrega || 0,
        
        // Configurações de tempo
        tempo_preparo_padrao: empresa.tempo_preparo_padrao || 0,
        tempo_por_km: empresa.tempo_por_km || 0,
        
        // API e outros
        google_maps_api_key: empresa.google_maps_api_key || '',
        ativo: empresa.ativo !== undefined ? empresa.ativo : true
      });
    }
  }, [empresa, reset]);

  const onSubmit = async (data: ConfigForm) => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : value.toString());
        }
      });
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (removerLogo) {
        formData.append('remover_logo', 'true');
      }

      const response = await api.put('/api/empresas/1', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Configurações salvas com sucesso!');
      
      // Atualizar estado local
      const empresaSalva = response.data?.empresa || response.data;
      if (empresaSalva) {
        setEmpresa(empresaSalva);
        if (empresaSalva.logo_url) {
          setLogoPreview(obterUrlImagem(empresaSalva.logo_url));
        } else if (removerLogo) {
          setLogoPreview(null);
        }
        setLogoFile(null);
        setRemoverLogo(false);
      }
      
      // Invalidar cache da empresa se existir
      try {
        const { cacheService } = await import('../../services/cacheService');
        cacheService.invalidate('empresa_1');
      } catch (e) {
        // Cache service pode não existir, não é crítico
      }
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      toast.error(error.response?.data?.error || error.response?.data?.erro || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 md:h-80">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 md:h-80 px-4">
        <div className="text-center bg-white dark:bg-gray-800 shadow rounded-lg p-6 max-w-md w-full">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <div className="text-red-500 text-lg font-semibold mb-2">
            Erro ao carregar configurações
          </div>
          <div className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
            {error.message}
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <Settings className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-blue-600" />
              Configurações da Empresa
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Configure todas as informações do seu estabelecimento
            </p>
          </div>
        </div>
      </div>

      {/* Menu de Abas */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap -mb-px overflow-x-auto" aria-label="Tabs">
            <button
              onClick={() => setAbaAtiva('basicas')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'basicas'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Building className="h-4 w-4" />
              <span className="hidden sm:inline">Informações Básicas</span>
              <span className="sm:hidden">Básicas</span>
            </button>
            <button
              onClick={() => setAbaAtiva('endereco')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'endereco'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span className="hidden sm:inline">Endereço</span>
              <span className="sm:hidden">Endereço</span>
            </button>
            <button
              onClick={() => setAbaAtiva('redes')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'redes'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">Redes Sociais</span>
              <span className="sm:hidden">Redes</span>
            </button>
            <button
              onClick={() => setAbaAtiva('descricao')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'descricao'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Descrição</span>
              <span className="sm:hidden">Descrição</span>
            </button>
            <button
              onClick={() => setAbaAtiva('frete')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'frete'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Truck className="h-4 w-4" />
              <span className="hidden sm:inline">Frete</span>
              <span className="sm:hidden">Frete</span>
            </button>
            <button
              onClick={() => setAbaAtiva('tecnicas')}
              className={`flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'tecnicas'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Técnicas</span>
              <span className="sm:hidden">Técnicas</span>
            </button>
          </nav>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 md:space-y-6">

        {/* Aba: Informações Básicas */}
        {abaAtiva === 'basicas' && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <Building className="h-5 w-5 md:h-6 md:w-6 text-blue-500" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  Informações Básicas
                </h3>
              </div>

              {/* Seção de Upload da Logo */}
              <div className="mb-6 p-4 md:p-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-750/50 backdrop-blur-xs transition-all">
                <label className="block text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Logomarca do Estabelecimento
                </label>
                <div className="flex flex-col sm:flex-row items-center gap-5">
                  <div className="relative group w-28 h-28 rounded-2xl overflow-hidden bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center shadow-xs flex-shrink-0">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo da Empresa"
                        className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400 p-2 text-center">
                        <ImageIcon className="w-8 h-8 mb-1 text-gray-400" />
                        <span className="text-[10px] text-gray-500 font-medium">Sem Logo</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-2.5 text-center sm:text-left">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs md:text-sm font-semibold rounded-xl shadow-xs transition-all">
                        <Upload className="w-4 h-4" />
                        <span>{logoPreview ? 'Alterar Logo' : 'Enviar Logo'}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                      </label>

                      {logoPreview && (
                        <button
                          type="button"
                          onClick={handleRemoverLogo}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 text-xs md:text-sm font-semibold rounded-xl border border-rose-200 dark:border-rose-800 transition-all active:scale-95"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Remover Logo</span>
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] md:text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      Formatos aceitos: <strong>PNG, JPG ou WebP</strong> (até 5MB). A logo será exibida no Cardápio Digital e no topo do sistema.
                    </p>
                  </div>
                </div>
              </div>
            
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Nome do Estabelecimento <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('nome', { required: 'Nome da empresa é obrigatório' })}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="Ex: JA FOI DELIVERY"
                    />
                  </div>
                  {errors.nome && (
                    <p className="mt-1 text-xs md:text-sm text-red-600">{errors.nome.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    CNPJ
                  </label>
                  <input
                    {...register('cnpj')}
                    type="text"
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('email', { 
                        required: 'Email é obrigatório',
                        pattern: { 
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 
                          message: 'Email inválido' 
                        } 
                      })}
                      type="email"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="contato@empresa.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs md:text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Telefone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('telefone')}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    WhatsApp
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('whatsapp')}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Tema da Interface
                  </label>
                  <select
                    {...register('tema')}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  >
                    <option value="dark">Escuro</option>
                    <option value="light">Claro</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aba: Endereço */}
        {abaAtiva === 'endereco' && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <MapPin className="h-5 w-5 md:h-6 md:w-6 text-green-500" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  Endereço Completo
                </h3>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Endereço Resumido
                  </label>
                  <input
                    {...register('endereco')}
                    type="text"
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                    placeholder="Ex: Rua das Flores, 123"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Endereço Completo
                  </label>
                  <textarea
                    {...register('endereco_completo')}
                    rows={3}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    placeholder="Endereço completo para cálculo de entrega"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      CEP
                    </label>
                    <input
                      {...register('cep')}
                      type="text"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="00000-000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Cidade
                    </label>
                    <input
                      {...register('cidade')}
                      type="text"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="Sua cidade"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Estado
                    </label>
                    <input
                      {...register('estado')}
                      type="text"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="UF"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Bairro
                    </label>
                    <input
                      {...register('bairro')}
                      type="text"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="Nome do bairro"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Número
                    </label>
                    <input
                      {...register('numero')}
                      type="text"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="123"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Complemento
                    </label>
                    <input
                      {...register('complemento')}
                      type="text"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="Apto, sala, etc."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aba: Redes Sociais */}
        {abaAtiva === 'redes' && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <Globe className="h-5 w-5 md:h-6 md:w-6 text-purple-500" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  Redes Sociais e Site
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Instagram
                  </label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('instagram')}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="@seuinstagram"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Facebook
                  </label>
                  <div className="relative">
                    <Facebook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('facebook')}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="seufacebook"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Site
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('site')}
                      type="url"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="https://seusite.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aba: Descrição e Horários */}
        {abaAtiva === 'descricao' && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <FileText className="h-5 w-5 md:h-6 md:w-6 text-orange-500" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  Descrição e Horários
                </h3>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Descrição da Empresa
                  </label>
                  <textarea
                    {...register('descricao')}
                    rows={4}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    placeholder="Descreva seu estabelecimento, especialidades, etc."
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Horário de Atendimento
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('horario_atendimento')}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="Ex: Segunda a Domingo: 14h às 02h"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aba: Frete e Entrega */}
        {abaAtiva === 'frete' && (
          <>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <Truck className="h-5 w-5 md:h-6 md:w-6 text-red-500" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                    Configurações de Frete
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Valor Mínimo do Frete (R$)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                      <input
                        {...register('valor_frete_minimo', { valueAsNumber: true })}
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="5.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Valor por KM (R$)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                      <input
                        {...register('valor_por_km', { valueAsNumber: true })}
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="1.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Raio de Entrega (KM)
                    </label>
                    <div className="relative">
                      <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                      <input
                        {...register('raio_entrega', { valueAsNumber: true })}
                        type="number"
                        min="0"
                        className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="25"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <Clock className="h-5 w-5 md:h-6 md:w-6 text-indigo-500" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                    Configurações de Tempo
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Tempo de Preparo Padrão (min)
                    </label>
                    <input
                      {...register('tempo_preparo_padrao', { valueAsNumber: true })}
                      type="number"
                      min="0"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="30"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                      Tempo Adicional por KM (min)
                    </label>
                    <input
                      {...register('tempo_por_km', { valueAsNumber: true })}
                      type="number"
                      min="0"
                      step="0.1"
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="2.5"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Aba: Configurações Técnicas */}
        {abaAtiva === 'tecnicas' && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <Settings className="h-5 w-5 md:h-6 md:w-6 text-gray-500" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  Configurações Técnicas
                </h3>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Google Maps API Key
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5" />
                    <input
                      {...register('google_maps_api_key')}
                      type="text"
                      className="w-full pl-11 pr-3 md:pl-12 md:pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="AIzaSy..."
                    />
                  </div>
                  <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Necessário para cálculo automático de frete e distância
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('ativo')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-xs md:text-sm text-gray-700 dark:text-gray-300">
                    Estabelecimento ativo
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botão Salvar - Fixo na parte inferior */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg sticky bottom-0 z-10">
          <div className="px-4 py-4 md:py-5 sm:p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm md:text-base w-full sm:w-auto"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 md:h-5 md:w-5" />
                    Salvar Todas as Configurações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Informações do Sistema */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Info className="h-5 w-5 md:h-6 md:w-6 text-blue-500" />
            <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-white">
              Informações do Sistema
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">Versão</p>
              <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white">1.0.0</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">CNPJ</p>
              <p className="text-sm md:text-base font-medium text-gray-900 dark:text-white">
                {empresa?.cnpj || 'Não informado'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 md:p-4">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">Status</p>
              <div className="flex items-center gap-2">
                {empresa?.ativo ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
                <p className={`text-sm md:text-base font-medium ${empresa?.ativo ? 'text-green-600' : 'text-red-600'}`}>
                  {empresa?.ativo ? 'Ativo' : 'Inativo'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracoesPage;
